import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';

/**
 * Detecta si estamos en un entorno local (localhost o IP)
 */
function isLocalEnvironment(): boolean {
  const hostname = window.location.hostname;

  // Localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
    return true;
  }

  // Dirección IP
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return true;
  }

  return false;
}

/**
 * Obtiene la base URL del servidor (sin /api)
 */
export function getServerBaseURL(): string {
  // 1. Prioridad a variable de entorno explícita
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (envApiUrl && envApiUrl.trim().length > 0) {
    return envApiUrl.replace(/\/api\/?$/, '');
  }

  // 2. Si estamos en un dominio (no localhost ni IP), usar ese dominio como origen
  const { protocol, hostname, port } = window.location;
  if (!isLocalEnvironment()) {
    // Construir origin dinámico respetando protocolo y puerto (si existe)
    const origin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    return origin.replace(/\/api\/?$/, '');
  }

  // 3. Entorno local sin variable => fallback explícito al backend local
  return 'http://localhost:3000';
}

/**
 * Obtiene la URL completa de la API (incluye /api)
 */
export function getApiURL(): string {
  return `${getServerBaseURL()}/api`;
}

/**
 * Obtiene la base URL de la API
 */
function getBaseURL(): string {
  return getApiURL();
}

/**
 * Instancia de Axios configurada para KOMBU
 */
export const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor: Agrega JWT token si existe
 */
api.interceptors.request.use(
  (config) => {
    // Add JWT token for admin authenticated requests
    // Import here to avoid circular dependency
    const getToken = () => {
    try {
      const stored = localStorage.getItem('kombu-admin-auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.token;
      }
    } catch {
      return null;
    }
    return null;
  };

  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  try {
    const stored = localStorage.getItem('kombu-admin-auth-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      const selectedRestaurantId = parsed.state?.admin?.selected_restaurant_id;
      if (selectedRestaurantId) {
        config.headers['X-Admin-Restaurant-Id'] = selectedRestaurantId;
      }
    }
  } catch {
    // auth storage may be corrupted or missing; skip header injection
  }

  return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor: Manejo de errores comunes y autenticación
 */
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as Record<string, unknown>;
      const message = (data?.error as string) || '';

      switch (status) {
        case 401:
          if (window.location.pathname.startsWith('/admin') &&
            !window.location.pathname.startsWith('/admin/login')) {
            try {
              localStorage.removeItem('kombu-admin-auth-storage');
            } catch {
              // storage may be restricted
            }
            window.location.href = '/admin/login';
          }
          break;
        case 403:
          toast.error(message || 'Acceso prohibido');
          break;
        case 404:
          toast.error(message || 'Recurso no encontrado');
          break;
        case 413:
          toast.error('El archivo es demasiado grande. Máximo 50MB permitidos.');
          break;
        case 422:
          toast.error(message || 'Error de validación');
          break;
        case 500:
          toast.error(message || 'Error del servidor');
          break;
        default:
          toast.error(message || `Error HTTP ${status}`);
      }
    } else if (error.request) {
      toast.error('No se recibió respuesta del servidor');
    } else {
      toast.error('Error al configurar la petición');
    }

    return Promise.reject(error);
  }
);
